import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Facebook,
    Instagram,
    Twitter,
    Linkedin,
    Youtube,
    MapPin,
    Phone,
    Mail,
    Send,
} from "lucide-react";
import { useSettingsStore } from "../../store/settingsStore";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function Footer() {
    const { t } = useTranslation();
    const { settings } = useSettingsStore();
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const social = settings.social_media || {};

    const SOCIAL_DOMAINS = {
        facebook: "facebook.com",
        instagram: "instagram.com",
        twitter: "twitter.com",
        linkedin: "linkedin.com",
        youtube: "youtube.com",
    };
    const normalizeSocial = (url, platform) => {
        if (!url) return "";
        let u = String(url).trim();
        const expected = SOCIAL_DOMAINS[platform];
        if (expected && !u.toLowerCase().includes(expected)) {
            const re = new RegExp(`(www\\.)?${platform}(?=$|[/.\\-?#])`, "i");
            u = u.replace(re, `www.${expected}`);
        }
        if (!/^https?:\/\//i.test(u)) u = "https://" + u;
        return u;
    };

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            const lang = "tr";
            const { data } = await api.post("/newsletter/subscribe", {
                email: email.trim(),
                source: "footer",
                lang,
            });
            if (data?.status === "already_subscribed") {
                toast.success("Zaten kayıtlısınız ✓");
            } else {
                toast.success(t("newsletter.title") + " ✓");
            }
            setEmail("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <footer
            data-testid="public-footer"
            className="bg-dark-navy text-white/85 mt-auto"
        >
            <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
                {/* Brand */}
                <div className="lg:col-span-2 space-y-4">
                    <span className="logo-badge bg-white border border-white/10">
                        <img
                            src={settings.logo_url}
                            alt="Otel Pro"
                            className="h-9 w-auto object-contain"
                        />
                    </span>
                    <p className="text-sm leading-relaxed text-white/70 max-w-sm">
                        {t("tagline")}. Sektörün veri merkezi, interaktif araçlar ve
                        yıldız standartları için tek başvuru noktası.
                    </p>
                    <div className="flex items-center gap-3 pt-2">
                        {social.facebook && (
                            <a href={normalizeSocial(social.facebook, "facebook")} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/70 hover:text-teal" data-testid="footer-social-facebook"><Facebook className="w-5 h-5" /></a>
                        )}
                        {social.instagram && (
                            <a href={normalizeSocial(social.instagram, "instagram")} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white/70 hover:text-teal" data-testid="footer-social-instagram"><Instagram className="w-5 h-5" /></a>
                        )}
                        {social.twitter && (
                            <a href={normalizeSocial(social.twitter, "twitter")} target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="text-white/70 hover:text-teal" data-testid="footer-social-twitter"><Twitter className="w-5 h-5" /></a>
                        )}
                        {social.linkedin && (
                            <a href={normalizeSocial(social.linkedin, "linkedin")} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-white/70 hover:text-teal" data-testid="footer-social-linkedin"><Linkedin className="w-5 h-5" /></a>
                        )}
                        {social.youtube && (
                            <a href={normalizeSocial(social.youtube, "youtube")} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="text-white/70 hover:text-teal" data-testid="footer-social-youtube"><Youtube className="w-5 h-5" /></a>
                        )}
                        {!social.facebook && !social.instagram && !social.linkedin && (
                            <>
                                <span className="text-white/40 text-xs uppercase tracking-wider">{t("footer.follow")}</span>
                                <Linkedin className="w-5 h-5 text-white/40" />
                                <Instagram className="w-5 h-5 text-white/40" />
                            </>
                        )}
                    </div>
                </div>

                {/* Platform */}
                <div>
                    <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">{t("footer.platform")}</h4>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/veri-merkezi" className="hover:text-teal" data-testid="footer-link-data">{t("nav.data")}</Link></li>
                        <li><Link to="/yildiz-standartlari" className="hover:text-teal" data-testid="footer-link-stars">{t("nav.stars")}</Link></li>
                        <li><Link to="/blog" className="hover:text-teal" data-testid="footer-link-blog">{t("nav.blog")}</Link></li>
                        <li><Link to="/basari-hikayeleri" className="hover:text-teal" data-testid="footer-link-stories">{t("nav.stories")}</Link></li>
                        <li><Link to="/hakkimizda" className="hover:text-teal" data-testid="footer-link-about">{t("nav.about")}</Link></li>
                    </ul>
                </div>

                {/* Tools */}
                <div>
                    <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">{t("footer.tools_section")}</h4>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/araclar/maliyet-hesaplayici" className="hover:text-teal">{t("tools.cost")}</Link></li>
                        <li><Link to="/araclar/roi-hesaplayici" className="hover:text-teal">{t("tools.roi")}</Link></li>
                        <li><Link to="/araclar/personel-planlayici" className="hover:text-teal">{t("tools.staff")}</Link></li>
                        <li><Link to="/araclar/enerji-hesaplayici" className="hover:text-teal">{t("tools.energy")}</Link></li>
                        <li><Link to="/araclar/doluluk-tahmincisi" className="hover:text-teal">{t("tools.occupancy")}</Link></li>
                    </ul>
                </div>

                {/* Contact + Newsletter */}
                <div>
                    <h4 className="text-white text-sm font-semibold uppercase tracking-wider mb-4">{t("footer.contact_section")}</h4>
                    <ul className="space-y-3 text-sm text-white/75">
                        <li className="flex items-start gap-2">
                            <Phone className="w-4 h-4 mt-0.5 text-teal" />
                            <a href={`tel:${(settings.phone || "").replace(/\s/g, "")}`} className="hover:text-teal" data-testid="footer-phone">{settings.phone}</a>
                        </li>
                        <li className="flex items-start gap-2">
                            <Mail className="w-4 h-4 mt-0.5 text-teal" />
                            <a href={`mailto:${settings.email}`} className="hover:text-teal" data-testid="footer-email">{settings.email}</a>
                        </li>
                        {settings.address && (
                            <li className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 mt-0.5 text-teal" />
                                <span>{settings.address}</span>
                            </li>
                        )}
                    </ul>
                    <form
                        id="newsletter"
                        onSubmit={handleSubscribe}
                        className="mt-6 flex items-stretch rounded-md overflow-hidden border border-white/15 bg-white/5"
                        data-testid="footer-newsletter-form"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={t("newsletter.placeholder")}
                            className="flex-1 bg-transparent text-white placeholder:text-white/50 px-3 py-2 text-sm outline-none"
                            required
                            data-testid="footer-newsletter-email"
                        />
                        <button
                            type="submit"
                            disabled={submitting}
                            className="bg-gold hover:bg-gold-700 disabled:opacity-60 text-dark-navy px-3 inline-flex items-center justify-center"
                            aria-label="Subscribe"
                            data-testid="footer-newsletter-submit"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>

            {/* Brands cross-link strip — rel=nofollow, no JSON-LD sameAs */}
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/55">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="footer-brands-strip">
                        <span className="uppercase tracking-wider text-white/40">{t("footer.brands_section")}:</span>
                        <a href="https://otel-urunleri.com.tr" rel="nofollow" target="_blank" className="hover:text-teal">otel-urunleri.com.tr</a>
                        <a href="https://otel-malzemeleri.com" rel="nofollow" target="_blank" className="hover:text-teal">otel-malzemeleri.com</a>
                        <a href="https://otel-ekipmanlari.com" rel="nofollow" target="_blank" className="hover:text-teal">otel-ekipmanlari.com</a>
                    </div>
                    <span data-testid="footer-copyright">© {new Date().getFullYear()} Otel Pro · {t("footer.rights")}</span>
                </div>
            </div>
        </footer>
    );
}
